import { useState } from 'react'
import { CheckCircle2, AlertTriangle, BarChart3, Shield, Zap, FileText, Clock, Play } from 'lucide-react'

export default function AuditToolPage() {
  const [stats] = useState({
    auditsConducted: 47,
    complianceScore: '94%',
    findingsResolved: '156/162',
    lastAuditDate: '2 days ago',
  })

  const auditFrameworks = [
    {
      name: 'NIST SP 800-53',
      category: 'Federal Standard',
      description: 'Comprehensive security controls for federal systems',
      controls: 218,
      status: 'Ready',
      color: 'from-blue-600 to-blue-500',
      dataSource: ['Security', 'Access Control', 'Audit Logs', 'Policy'],
    },
    {
      name: 'NIST SP 800-171 & CMMC 2.0',
      category: 'DoD Contractor',
      description: 'Controlled Unclassified Information (CUI) protection',
      controls: 110,
      status: 'Ready',
      color: 'from-cyan-600 to-cyan-500',
      dataSource: ['Network', 'Access Control', 'Encryption', 'Incidents'],
    },
    {
      name: 'NIST CSF 2.0',
      category: 'Voluntary Framework',
      description: 'Core functions: Govern, Identify, Protect, Detect, Respond, Recover',
      controls: 156,
      status: 'Ready',
      color: 'from-green-600 to-green-500',
      dataSource: ['All Systems', 'Risk Assessment', 'Monitoring'],
    },
    {
      name: 'NIST SP 800-37 (RMF)',
      category: 'Risk Management',
      description: '7-step Authorization to Operate (ATO) process',
      controls: 45,
      status: 'Ready',
      color: 'from-purple-600 to-purple-500',
      dataSource: ['All Modules', 'Risk Register', 'Assessment Results'],
    },
    {
      name: 'NIST Privacy Framework',
      category: 'Privacy Controls',
      description: 'Privacy risk management and data protection',
      controls: 73,
      status: 'Ready',
      color: 'from-pink-600 to-pink-500',
      dataSource: ['Data Management', 'Access Logs', 'Incident Reports'],
    },
    {
      name: 'SOC 2 Type II',
      category: 'SaaS Compliance',
      description: 'Service Organization Control for security and availability',
      controls: 64,
      status: 'Ready',
      color: 'from-indigo-600 to-indigo-500',
      dataSource: ['Operations', 'Change Logs', 'Incident Tracking', 'Monitoring'],
    },
    {
      name: 'ISO 27001',
      category: 'International Standard',
      description: 'Information Security Management System',
      controls: 114,
      status: 'Ready',
      color: 'from-orange-600 to-orange-500',
      dataSource: ['All Modules', 'Risk Assessment', 'Training Logs'],
    },
    {
      name: 'HIPAA',
      category: 'Healthcare Compliance',
      description: 'Protected Health Information (PHI) security and privacy',
      controls: 88,
      status: 'Ready',
      color: 'from-red-600 to-red-500',
      dataSource: ['Data Protection', 'Access Control', 'Encryption', 'Audit Logs'],
    },
  ]

  const operationalAudits = [
    {
      name: 'Configuration Audit',
      description: 'Review firewall, server, and router configurations',
      checks: 47,
      color: 'from-teal-600 to-teal-500',
      icon: Shield,
    },
    {
      name: 'Access Control Audit',
      description: 'Principle of Least Privilege verification',
      checks: 156,
      color: 'from-violet-600 to-violet-500',
      icon: CheckCircle2,
    },
    {
      name: 'Encryption Audit',
      description: 'Data at rest and in transit encryption verification',
      checks: 78,
      color: 'from-emerald-600 to-emerald-500',
      icon: Zap,
    },
    {
      name: 'Incident Response Audit',
      description: 'Verify incident detection, response, and recovery procedures',
      checks: 34,
      color: 'from-amber-600 to-amber-500',
      icon: AlertTriangle,
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-8 text-white shadow-2xl border border-purple-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <CheckCircle2 className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Comprehensive Audit Tool</h1>
                </div>
                <p className="text-purple-100 text-lg font-semibold">Multi-Framework Compliance & Security Auditing</p>
                <p className="text-purple-200 text-sm mt-1">NIST • SOC 2 • ISO 27001 • HIPAA • Configuration • Access Control • Encryption</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.auditsConducted}</div>
                <div className="text-sm text-purple-100">Audits Conducted</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Compliance Score', value: stats.complianceScore, icon: BarChart3, color: 'from-green-600 to-green-500' },
            { label: 'Findings Resolved', value: stats.findingsResolved, icon: CheckCircle2, color: 'from-blue-600 to-blue-500' },
            { label: 'Frameworks Active', value: '8', icon: Shield, color: 'from-purple-600 to-purple-500' },
            { label: 'Last Audit', value: stats.lastAuditDate, icon: Clock, color: 'from-orange-600 to-orange-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* NIST & Compliance Frameworks */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Compliance Frameworks</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {auditFrameworks.map((framework, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className={`h-1 w-full bg-gradient-to-r ${framework.color} rounded-full mb-4 shadow-lg`}></div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-white text-lg">{framework.name}</h3>
                    <p className="text-blue-300 text-sm">{framework.category}</p>
                  </div>
                  <span className="px-3 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded-full">
                    {framework.status}
                  </span>
                </div>
                <p className="text-blue-300 text-sm mb-4">{framework.description}</p>
                <div className="mb-4">
                  <p className="text-cyan-400 text-xs font-bold mb-2">{framework.controls} Controls</p>
                  <div className="flex flex-wrap gap-2">
                    {framework.dataSource.map((source, sidx) => (
                      <span key={sidx} className="text-xs bg-slate-700/50 text-blue-300 px-2 py-1 rounded border border-blue-500/20">
                        {source}
                      </span>
                    ))}
                  </div>
                </div>
                <button className={`w-full bg-gradient-to-r ${framework.color} hover:shadow-lg transition-all px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center justify-center space-x-2`}>
                  <Play className="w-4 h-4" />
                  <span>Run Audit</span>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Operational Audits */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Technical & Operational Audits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {operationalAudits.map((audit, idx) => {
              const Icon = audit.icon
              return (
                <div key={idx} className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1">
                  <div className={`h-1 w-full bg-gradient-to-r ${audit.color} rounded-full mb-4 shadow-lg`}></div>
                  <div className={`bg-gradient-to-br ${audit.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{audit.name}</h3>
                  <p className="text-blue-300 text-sm mb-4">{audit.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{audit.checks} Checks</span>
                    <button className={`bg-gradient-to-r ${audit.color} hover:shadow-lg transition-all px-3 py-1 rounded text-white text-xs font-bold`}>
                      Run
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Audit Evidence Requirements */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
            <FileText className="w-6 h-6 text-blue-400" />
            <span>Required Evidence & Documentation</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { title: 'Policies & Documentation', items: ['Incident Response Plan', 'Data Handling Policy', 'Access Control Policy', 'Security Policy'] },
              { title: 'Training & Awareness', items: ['Training Completion Logs', 'Certification Records', 'Awareness Scores', 'Training History'] },
              { title: 'Physical & Digital Security', items: ['Server Room Access', 'Surveillance Logs', 'Encryption Status', 'Device Hardening'] },
              { title: 'Encryption', items: ['At Rest Verification', 'In Transit Verification', 'Key Management', 'Certificate Status'] },
              { title: 'Business Continuity', items: ['Backup Schedules', 'Backup Verification', 'Disaster Recovery Tests', 'RTO/RPO Metrics'] },
            ].map((category, idx) => (
              <div key={idx} className="p-4 bg-slate-700/50 rounded-lg border border-blue-500/20">
                <p className="text-blue-400 font-bold text-sm mb-3">{category.title}</p>
                <ul className="space-y-2">
                  {category.items.map((item, iidx) => (
                    <li key={iidx} className="text-blue-300 text-xs flex items-start space-x-2">
                      <span className="text-cyan-400 mt-1">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Data Sources for Audits */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-4">Audit Data Sources (Connected Systems)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              'Help Desk', 'CRM', 'HR/Payroll', 'Finance', 'Projects', 'Inventory',
              'Chat/Email', 'Meetings', 'Documents', 'Network', 'Security', 'Analytics',
              'Cloud', 'Calendar', 'Training', 'Auditor', 'EDR', 'Device Protection',
              'Call Logs', 'Social Media', 'Integrations', 'API', 'Backend', 'Settings'
            ].map((source, idx) => (
              <div key={idx} className="px-4 py-3 bg-slate-700/50 rounded-lg border border-green-500/20 text-center">
                <p className="text-green-300 text-sm font-semibold">{source}</p>
                <p className="text-green-400 text-xs mt-1">✓ Connected</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
